"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useCart } from "../data/CartContext";
import { track } from "../lib/track";

type TrustItem = { label: string; icon?: React.ReactNode };

interface FeaturedProductCardProps {
  imageSrc: string;
  title: string;
  price: string;
  size: string;
  slug: string;
  id: string;
  tagline?: string;
  badge?: string;
  trust?: TrustItem[];
}

const badgeStyles: Record<string, string> = {
  "Best Seller": "bg-amber-500 text-white rounded-full",
  "Hot Item": "bg-orange-500 text-white rounded-full",
  "Top Pick": "bg-green-700 text-white rounded-lg",
  "Limited Batch": "bg-teal-500 text-white rounded-md",
  "100% Natural": "bg-emerald-700 text-white rounded-xl",
};

export default function FeaturedProductCard({
  imageSrc,
  title,
  price,
  size,
  slug,
  id,
  tagline,
  badge,
  trust = [],
}: FeaturedProductCardProps) {
  const { addToCart } = useCart();
  const [showNote, setShowNote] = useState(false);

  // `price` is a display string ("£30.00 GBP"). Schema.org needs a bare number,
  // so parse once: NaN means "don't emit a price" (the cart keeps its 0 fallback).
  const parsedPrice = Number(String(price).replace(/[^\d.-]/g, ""));
  const numericPrice = parsedPrice || 0;

  const handleAddToCart = () => {
    addToCart({ id, title, price, qty: 1, totalPrice: numericPrice });
    track(
      "add_to_cart",
      { label: `Added 1 × ${title}`, component: "FeaturedProductCard" },
      { event: "add_to_cart", label: `Added 1 × ${title}`, component: "FeaturedProductCard", meta: { timestamp: Date.now() } }
    );
    setShowNote(true);
    setTimeout(() => setShowNote(false), 4000);
  };

  return (
    <article
      className="bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row"
      itemScope
      itemType="https://schema.org/Product"
    >
      <meta itemProp="sku" content={id} />

      {/* Image */}
      <Link href={`/shop/${slug}`} className="relative md:w-2/5 h-56 md:h-72 block shrink-0" itemProp="url">
        <Image
          src={imageSrc}
          alt={`${title} – ${size}`}
          fill
          sizes="(max-width: 768px) 100vw, 40vw"
          className="object-cover"
          priority
        />
        <meta itemProp="image" content={imageSrc} />
        {badge && (
          <span className={`absolute top-3 left-3 text-sm px-3 py-1 font-semibold shadow ${badgeStyles[badge] ?? "bg-[var(--color-brand)] text-white rounded-md"}`}>
            {badge}
          </span>
        )}
      </Link>

      {/* Info */}
      <div className="p-6 flex flex-col justify-center flex-1">
        <p className="text-xs font-semibold uppercase tracking-widest text-[var(--color-brand)] mb-1">
          Featured
        </p>
        <h2
          className="text-xl md:text-2xl font-extrabold text-[var(--color-text-brand)] leading-tight"
          itemProp="name"
        >
          {title}
        </h2>
        {tagline && (
          <p className="text-sm text-gray-600 mt-2 leading-relaxed">{tagline}</p>
        )}

        {trust.length > 0 && (
          <ul className="mt-4 space-y-1.5">
            {trust.slice(0, 3).map((t, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                <span aria-hidden className="text-base shrink-0">{t.icon ?? "✅"}</span>
                <span>{t.label}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <p
            className="text-lg font-semibold text-[var(--color-text-brand)]"
            itemProp="offers"
            itemScope
            itemType="https://schema.org/Offer"
          >
            <meta itemProp="priceCurrency" content="GBP" />
            {!Number.isNaN(parsedPrice) && (
              <meta itemProp="price" content={parsedPrice.toFixed(2)} />
            )}
            <span>{price}</span>
            <meta itemProp="availability" content="https://schema.org/InStock" />
          </p>
          <button
            onClick={handleAddToCart}
            className="bg-[var(--color-text-brand)] text-white py-2 px-5 rounded-md hover:bg-[var(--color-text-brand-contrast)] transition font-medium"
          >
            Add to Cart
          </button>
          <Link
            href={`/shop/${slug}`}
            className="text-sm text-[var(--color-text-brand)] underline underline-offset-4 hover:opacity-70"
          >
            See details →
          </Link>
        </div>

        {showNote && (
          <div className="mt-3 text-sm bg-green-50 border border-[var(--color-accent)] rounded-lg py-2 px-3 text-[var(--color-accent)]">
            Added <strong>{title}</strong> to cart.{" "}
            <Link href="/shop/cart" className="underline text-[var(--color-accent)]">
              View cart →
            </Link>
          </div>
        )}
      </div>
    </article>
  );
}
